import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { TestResultDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import type { TestResultsRegisterResponseRow } from '@risksmart-app/drizzle/src/dto';
import type {
  GetTestResultsQuery,
  Test_Result_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetTestResultsDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import useEntityWhereFilter from 'src/hooks/useEntityWhereFilter';
import { createQueryHook } from 'src/utils';

import { TEST_RESULT_DATE_PREDICATES } from '@/hooks/queries/dashboardDatePredicates';
import {
  buildDashboardWhere,
  type DashboardFilterArgs,
  normaliseArrayFilter,
} from '@/hooks/queries/dashboardFilterArgs';

type InternalArgs = UseGetTestResultsArgs & {
  entityIds?: string[];
};

const useGetTestResultsGraphqlVariables = (args: InternalArgs) => {
  const { user } = useRisksmartUser();
  const entityWhere = useEntityWhereFilter<Test_Result_Bool_Exp>(
    Parent_Type_Enum.TestResult,
    {
      RatingType: { _in: ['assessment', 'rating'] },
    }
  );

  const { where } = buildDashboardWhere({
    base: entityWhere,
    filters: args,
    userId: user?.userId,
    datePredicates: TEST_RESULT_DATE_PREDICATES,
    tagsWhere: (ids) => ({
      parent: { tags: { TagTypeId: { _in: ids } } },
    }),
    departmentsWhere: (ids) => ({
      parent: { departments: { DepartmentTypeId: { _in: ids } } },
    }),
    ownershipWhere: (conditions) => ({
      parent: conditions.controlFilterConditions,
    }),
  });

  return { where };
};

const useGetTestResultsBase = createQueryHook<
  InternalArgs,
  TestResultsRegisterResponseRow[],
  GetTestResultsQuery
>({
  trpcQueryOptions: (
    trpc,
    { entityIds, tagTypeIds, departmentTypeIds, dateRange, ownership }
  ) =>
    trpc.frontend.testResult.testResults.queryOptions({
      entityIds,
      tagTypeIds: normaliseArrayFilter(tagTypeIds),
      departmentTypeIds: normaliseArrayFilter(departmentTypeIds),
      dateRange,
      ownership,
    }),
  mapTrpcDataToGraphQL: (data) => ({ test_result: data }),
  graphqlDocument: GetTestResultsDocument,
  graphqlVariables: useGetTestResultsGraphqlVariables,
});

export type UseGetTestResultsArgs =
  DashboardFilterArgs<TestResultDashboardDateField>;

/**
 * Fetches test results with automatic entity filtering for both GraphQL and
 * tRPC paths. Entity filtering uses useEntityFilter context internally —
 * consumers don't need to pass entityIds. Dashboard filters (COI-691) are all
 * optional; tag and department traverse to the parent control.
 */
export const useGetTestResults = (params: {
  queryArgs: UseGetTestResultsArgs;
  shouldSkip?: boolean;
  suppressErrorNotification?: boolean;
}) => {
  const { entityIds } = useEntityFilter();

  return useGetTestResultsBase({
    queryArgs: {
      ...params.queryArgs,
      entityIds: entityIds?.length ? entityIds : undefined,
    },
    shouldSkip: params.shouldSkip,
    suppressErrorNotification: params.suppressErrorNotification,
  });
};
